import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

export const REPORTING_GROUP_LEVELS = [
  'location',
  'specialty',
  'microphone',
  'template',
  'provider',
] as const;

/**
 * Timezones the dashboard can bucket data in — appointment timestamps are
 * converted to local dates in this zone before weekly aggregation.
 */
export const REPORTING_TIMEZONES = [
  'UTC',
  'Asia/Jakarta',
  'America/Los_Angeles',
  'America/New_York',
] as const;

export type ReportingTimezone = (typeof REPORTING_TIMEZONES)[number];

/** Time bucket granularity for the trend columns. */
export const REPORTING_GRAINS = ['day', 'week', 'month'] as const;

export type ReportingGrain = (typeof REPORTING_GRAINS)[number];

/** Maximum trend columns returned per grain; longer spans are clamped. */
export const REPORTING_GRAIN_MAX_BUCKETS: Record<ReportingGrain, number> = {
  day: 31,
  week: 26,
  month: 12,
};

export type ReportingGroupLevel = (typeof REPORTING_GROUP_LEVELS)[number];

/**
 * What each cell measures — a numerator/denominator pair over the
 * appointments in the bucket:
 * - usage: started (used Eluve) / all appointments
 * - completion: completed / started
 * - signing: signed / started
 */
export const REPORTING_METRICS = ['usage', 'completion', 'signing'] as const;

export type ReportingMetric = (typeof REPORTING_METRICS)[number];

/**
 * Sentinel used as the row key (and parent filter value) for rows whose
 * grouping dimension is NULL (e.g. appointments without a location).
 */
export const REPORTING_NONE_KEY = '(none)';

/** Raw appointment counts per bucket — every metric derives from these. */
const BucketCountsSchema = z.object({
  total: z.number(),
  started: z.number(),
  completed: z.number(),
  signed: z.number(),
});

export type ReportingBucketCounts = z.infer<typeof BucketCountsSchema>;

/** Distinct users active in a bucket, by funnel stage. */
const BucketUsersSchema = z.object({
  // ≥1 appointment of any status in the bucket
  scheduled: z.number(),
  // ≥1 completed appointment in the bucket
  completed: z.number(),
});

export type ReportingBucketUsers = z.infer<typeof BucketUsersSchema>;

const BucketCellSchema = z.object({
  bucketStart: z.string(),
  // Pair for the requested `metric` param. Superseded by `counts` (clients
  // switch metrics locally); kept so already-deployed bundles keep working.
  numerator: z.number(),
  denominator: z.number(),
  // null when the denominator is zero for that bucket
  pct: z.number().nullable(),
  // Absent only on servers predating client-side metric switching
  counts: BucketCountsSchema.optional(),
  // Distinct-user counts — only populated on `totals` cells (root loads on
  // servers that compute them); absent elsewhere
  users: BucketUsersSchema.optional(),
});

/** User-level attributes for provider rows (hover details), from user_facts. */
const UserDetailsSchema = z.object({
  specialty: z.string().nullable(),
  firstCompletedDate: z.string().nullable(),
  defaultTemplateName: z.string().nullable(),
});

export type ReportingUserDetails = z.infer<typeof UserDetailsSchema>;

const LevelRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  sublabel: z.string().nullable(),
  cells: z.array(BucketCellSchema),
  // Distinct users under this row across the whole window. Absent on
  // servers predating user counts.
  userCount: z.number().optional(),
  // Only present on provider-level rows
  details: UserDetailsSchema.optional(),
});

export type WeeklyAdoptionLevelRow = z.infer<typeof LevelRowSchema>;

const MetricPctsSchema = z.object({
  usage: z.number().nullable(),
  completion: z.number().nullable(),
  signing: z.number().nullable(),
});

const SummarySchema = z.object({
  providersTracked: z.number(),
  // Pair semantics as above: avgLatestPct is for the requested `metric`,
  // avgLatestPctByMetric lets clients switch without a refetch
  avgLatestPct: z.number().nullable(),
  avgLatestPctByMetric: MetricPctsSchema.optional(),
  // Per-provider average over the latest COMPLETE bucket (with the prior
  // bucket for a delta) — the running bucket is never eligible
  avgCompleteByMetric: z
    .object({
      bucketStart: z.string(),
      previousBucketStart: z.string().nullable(),
      current: MetricPctsSchema,
      previous: MetricPctsSchema.nullable(),
    })
    .nullable()
    .optional(),
  // Providers with >=1 started appointment in the latest COMPLETE bucket
  // (the running bucket would always undercount), plus the prior bucket
  // for a delta. Null when the window has no complete bucket.
  activeProviders: z
    .object({
      bucketStart: z.string(),
      count: z.number(),
      previousBucketStart: z.string().nullable(),
      previousCount: z.number().nullable(),
    })
    .nullable()
    .optional(),
  // Tenant-wide lifecycle_status split of tenant_users (not scoped to the
  // date window or filters); NULL statuses count as created
  lifecycle: z
    .object({
      created: z.number(),
      onboarded: z.number(),
      active: z.number(),
      churned: z.number(),
    })
    .optional(),
});

const FiltersSchema = z.object({
  locations: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
  specialties: z.array(z.string()),
  // Absent on servers predating provider/template/microphone filters
  providers: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  templates: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  microphones: z.array(z.string()).optional(),
});

const WeeklyAdoptionLevelResponseSchema = z.object({
  bucketStarts: z.array(z.string()),
  rows: z.array(LevelRowSchema),
  // Only present when includeMeta=true (root-level loads)
  summary: SummarySchema.optional(),
  filters: FiltersSchema.optional(),
  // Filtered tenant-wide totals per bucket — feeds the top-line trend chart
  totals: z.array(BucketCellSchema).optional(),
  // When the tenant's flattened facts were last built (root loads only)
  dataAsOf: z.string().nullable().optional(),
  // BigQuery round-trip for this level query. `cached` marks a response
  // served from the shared result cache (durationMs is the original run's).
  queryStats: z
    .object({
      durationMs: z.number(),
      rowCount: z.number(),
      cached: z.boolean().optional(),
    })
    .optional(),
});

const ExportRowSchema = z.object({
  // Label per requested groupBy dim, in request order (e.g. location name,
  // then specialty)
  groups: z.array(z.string()),
  providerName: z.string(),
  providerEmail: z.string().nullable(),
  // Whole-window rollup across all buckets
  aggregate: BucketCountsSchema,
  // Aligned with bucketStarts; null = no appointments that bucket
  buckets: z.array(BucketCountsSchema.nullable()),
});

const ReportingExportResponseSchema = z.object({
  bucketStarts: z.array(z.string()),
  rows: z.array(ExportRowSchema),
});

export type ReportingExportRow = z.infer<typeof ExportRowSchema>;
export type ReportingExportResponse = z.infer<
  typeof ReportingExportResponseSchema
>;

const ReportingStatusSchema = z.object({
  // missing: never flattened; stale: extract schema changed since the last
  // build; ready: facts are queryable
  state: z.enum(['missing', 'stale', 'ready']),
  flattenedAt: z.string().nullable(),
  // Soft floor of the flattened data. Reporting covers 2025 onward, but the
  // floor sits at 2024-12-31 (UTC) so local 2025-01-01 appointments in
  // ahead-of-UTC timezones aren't clipped — minDate may predate 2025-01-01.
  minDate: z.string(),
});

export type ReportingStatus = z.infer<typeof ReportingStatusSchema>;

export type WeeklyAdoptionLevelResponse = z.infer<
  typeof WeeklyAdoptionLevelResponseSchema
>;

export const reportingContract = c.router(
  {
    /**
     * One grouping level of the weekly-adoption breakdown, aggregated
     * server-side. Expanding a row fetches the next level scoped by
     * parentLocationId / parentSpecialty, so only visible data is loaded.
     */
    getWeeklyAdoptionLevel: {
      method: 'GET',
      path: '/weekly-adoption-level',
      pathParams: z.object({
        tenantId: z.string().uuid(),
      }),
      query: z.object({
        // Inclusive local-date window; spans longer than 26 weeks are
        // clamped server-side
        from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        timezone: z.enum(REPORTING_TIMEZONES).default('UTC'),
        grain: z.enum(REPORTING_GRAINS).default('week'),
        metric: z.enum(REPORTING_METRICS).default('usage'),
        // Global filters: comma-separated lists, OR semantics within each
        locationIds: z
          .string()
          .min(1)
          .optional()
          .transform((value) => value?.split(',').filter(Boolean)),
        specialties: z
          .string()
          .min(1)
          .optional()
          .transform((value) => value?.split(',').filter(Boolean)),
        providerIds: z
          .string()
          .min(1)
          .optional()
          .transform((value) => value?.split(',').filter(Boolean)),
        templateIds: z
          .string()
          .min(1)
          .optional()
          .transform((value) => value?.split(',').filter(Boolean)),
        microphones: z
          .string()
          .min(1)
          .optional()
          .transform((value) => value?.split(',').filter(Boolean)),
        // What to group this level's rows by
        level: z.enum(REPORTING_GROUP_LEVELS),
        // Scope from expanded ancestor rows (REPORTING_NONE_KEY targets NULLs)
        parentLocationId: z.string().min(1).optional(),
        parentSpecialty: z.string().min(1).optional(),
        parentMicrophone: z.string().min(1).optional(),
        parentTemplateId: z.string().min(1).optional(),
        // Only count appointments on/after each provider's onboarded date
        // (lifecycle transition or first completed appointment). Absent
        // means 'true' — filtering is the default view.
        onboardedOnly: z.enum(['true', 'false']).optional(),
        // Include summary stat cards + filter dropdown options (root loads)
        includeMeta: z.literal('true').optional(),
      }),
      responses: {
        200: WeeklyAdoptionLevelResponseSchema,
        // Facts not built (or stale) for this tenant — a flatten has been
        // enqueued; poll status and retry
        409: z.object({
          status: z.literal('preparing'),
        }),
        503: z.object({
          message: z.string(),
        }),
      },
      summary:
        'Weekly note adoption aggregated at one grouping level (location / specialty / microphone / provider), sourced from the flattened BigQuery facts',
    },
    /**
     * Flat per-provider export: one row per provider (scoped by the same
     * filters as the dashboard) carrying the group labels, the whole-window
     * rollup, and every bucket's raw counts — the client renders it to CSV.
     */
    exportReporting: {
      method: 'GET',
      path: '/export',
      pathParams: z.object({
        tenantId: z.string().uuid(),
      }),
      query: z.object({
        from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        timezone: z.enum(REPORTING_TIMEZONES).default('UTC'),
        grain: z.enum(REPORTING_GRAINS).default('week'),
        locationIds: z
          .string()
          .min(1)
          .optional()
          .transform((value) => value?.split(',').filter(Boolean)),
        specialties: z
          .string()
          .min(1)
          .optional()
          .transform((value) => value?.split(',').filter(Boolean)),
        providerIds: z
          .string()
          .min(1)
          .optional()
          .transform((value) => value?.split(',').filter(Boolean)),
        templateIds: z
          .string()
          .min(1)
          .optional()
          .transform((value) => value?.split(',').filter(Boolean)),
        microphones: z
          .string()
          .min(1)
          .optional()
          .transform((value) => value?.split(',').filter(Boolean)),
        // Comma-separated non-provider dims that become leading columns
        groupBy: z
          .string()
          .min(1)
          .optional()
          .transform((value) => value?.split(',').filter(Boolean)),
        onboardedOnly: z.enum(['true', 'false']).optional(),
      }),
      responses: {
        200: ReportingExportResponseSchema,
        409: z.object({
          status: z.literal('preparing'),
        }),
        503: z.object({
          message: z.string(),
        }),
      },
      summary:
        'Flat per-provider reporting export (group labels + aggregate + per-bucket counts) for CSV download',
    },
    getReportingStatus: {
      method: 'GET',
      path: '/status',
      pathParams: z.object({
        tenantId: z.string().uuid(),
      }),
      responses: {
        200: ReportingStatusSchema,
        503: z.object({
          message: z.string(),
        }),
      },
      summary: "State of the tenant's flattened reporting facts",
    },
    refreshReporting: {
      method: 'POST',
      path: '/refresh',
      pathParams: z.object({
        tenantId: z.string().uuid(),
      }),
      body: z.object({
        // Rebuild from scratch instead of incrementally from the watermark
        force: z.boolean().optional(),
      }),
      responses: {
        202: z.object({
          enqueued: z.literal(true),
        }),
        503: z.object({
          message: z.string(),
        }),
      },
      summary:
        "Enqueue a flatten of the tenant's appointments into the reporting facts (concurrent requests coalesce)",
    },
  },
  {
    pathPrefix: '/:tenantId/reporting',
  },
);
